import { useRef, useState } from "react";
import { Upload, X, ImageIcon, Loader2, Link } from "lucide-react";
import { storage, BUCKET_ID, ENDPOINT, PROJECT_ID } from "../../../lib/appwrite";
import { ID } from "appwrite";
import { Button } from "./button";
import { Input } from "./input";

interface ImageUploadProps {
    value: string;          // current URL
    onChange: (url: string) => void;
    label?: string;
    placeholder?: string;
    aspectRatio?: "video" | "square" | "auto";
}

/**
 * ImageUpload — supports either:
 *  • Uploading a file → Appwrite Storage → public URL stored
 *  • Pasting an external URL directly
 */
export function ImageUpload({
    value,
    onChange,
    label = "Image",
    placeholder = "https://…",
    aspectRatio = "video",
}: ImageUploadProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const [mode, setMode] = useState<"upload" | "url">("upload");

    const handleFile = async (file: File) => {
        if (!file.type.startsWith("image/")) {
            setError("Please select an image file.");
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            setError("File must be under 10 MB.");
            return;
        }
        setError("");
        setUploading(true);
        try {
            const result = await storage.createFile(BUCKET_ID, ID.unique(), file);
            const url = `${ENDPOINT}/storage/buckets/${BUCKET_ID}/files/${result.$id}/view?project=${PROJECT_ID}`;
            onChange(url);
        } catch (e: any) {
            setError(e?.message ?? "Upload failed. Check bucket permissions.");
        } finally {
            setUploading(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file) handleFile(file);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) handleFile(file);
    };

    const aspectClass = aspectRatio === "video" ? "aspect-video" : aspectRatio === "square" ? "aspect-square" : "";

    return (
        <div className="space-y-2">
            {label && (
                <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">{label}</span>
                    <div className="flex items-center gap-1 bg-muted rounded-md p-0.5">
                        <button
                            type="button"
                            onClick={() => setMode("upload")}
                            className={`flex items-center gap-1 text-[11px] px-2 py-0.5 rounded transition-colors ${mode === "upload" ? "bg-background shadow-sm text-foreground font-medium" : "text-muted-foreground hover:text-foreground"}`}
                        >
                            <Upload className="w-3 h-3" /> Upload
                        </button>
                        <button
                            type="button"
                            onClick={() => setMode("url")}
                            className={`flex items-center gap-1 text-[11px] px-2 py-0.5 rounded transition-colors ${mode === "url" ? "bg-background shadow-sm text-foreground font-medium" : "text-muted-foreground hover:text-foreground"}`}
                        >
                            <Link className="w-3 h-3" /> URL
                        </button>
                    </div>
                </div>
            )}

            {/* URL mode */}
            {mode === "url" && (
                <div className="relative">
                    <ImageIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    <Input
                        className="pl-8 shadow-none h-9 text-xs"
                        placeholder={placeholder}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                    />
                </div>
            )}

            {/* Upload mode */}
            {mode === "upload" && !value && (
                <div
                    role="button"
                    tabIndex={0}
                    onClick={() => inputRef.current?.click()}
                    onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
                    onDrop={handleDrop}
                    onDragOver={(e) => e.preventDefault()}
                    className="border-2 border-dashed border-border rounded-md flex flex-col items-center justify-center gap-2 py-8 cursor-pointer hover:border-primary/40 hover:bg-muted/30 transition-colors select-none"
                >
                    {uploading
                        ? <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                        : <Upload className="w-6 h-6 text-muted-foreground" />}
                    <span className="text-xs text-muted-foreground text-center">
                        {uploading ? "Uploading…" : "Click or drag & drop an image"}
                    </span>
                    <span className="text-[11px] text-muted-foreground/60">PNG, JPG, WebP · max 10 MB</span>
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        onChange={handleInputChange}
                    />
                </div>
            )}

            {/* Preview (shown in both modes if value exists) */}
            {value && (
                <div className={`relative w-full ${aspectClass} overflow-hidden rounded-md border border-border bg-muted`}>
                    <img
                        src={value}
                        alt="Preview"
                        className="w-full h-full object-cover"
                        onError={(e) => { (e.target as HTMLImageElement).style.opacity = "0.3"; }}
                    />
                    <div className="absolute top-2 right-2 flex gap-1">
                        {mode === "upload" && (
                            <Button
                                type="button"
                                size="sm"
                                variant="secondary"
                                onClick={() => inputRef.current?.click()}
                                disabled={uploading}
                                className="h-7 text-xs shadow-none gap-1"
                            >
                                {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                                {uploading ? "…" : "Replace"}
                            </Button>
                        )}
                        <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            onClick={() => onChange("")}
                            className="h-7 w-7 p-0 shadow-none"
                        >
                            <X className="w-3.5 h-3.5" />
                        </Button>
                    </div>
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        onChange={handleInputChange}
                    />
                </div>
            )}

            {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
    );
}
